import { Button, Form, Modal, TableProps, Tag } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { apiCreateActivity, apiDeleteActivity, apiFilterActivities } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import ActivityForm from '../../components/outlet/ActivityForm'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { ACTIVITY_OUTCOME_OPTIONS, ACTIVITY_TYPE_OPTIONS, IOutletActivity } from '../../models/outlet.modal'
import { authSelector } from '../../redux/slices/authSlice'
import PermUtils from '../../utils/perm-utils'
import { toastError, toastSuccess } from '../../utils/toast'

const fmtDateTime = (v?: string | null) => {
    if (!v) return '—'
    const d = dayjs(v)
    if (!d.isValid() || d.year() < 2000) return '—'
    return d.format('DD/MM/YYYY HH:mm')
}

const labelFrom = (opts: { value: string, label: string }[], v?: string) => {
    if (!v) return '—'
    return opts.find(o => o.value === v)?.label ?? v
}

export default function TabActivity({ outletId }: { outletId: string }) {
    const [data, setData] = useState<IOutletActivity[]>([])
    const [total, setTotal] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [isOpenCreate, setIsOpenCreate] = useState<boolean>(false)
    const [form] = Form.useForm()
    const { setIsLoading } = useLoading()
    const authState = useSelector(authSelector)
    const currentUserId = Number(authState?.account?.id ?? 0)
    const canAdd = PermUtils.hasAnyAuthority(authState?.account?.listPermission, [PermUtils.canAddOutletActivity])
    const canDelete = PermUtils.hasAnyAuthority(authState?.account?.listPermission, [PermUtils.canDeleteOutletActivity])

    const fetchData = useCallback(async () => {
        if (!outletId) return
        try {
            setIsLoading(true)
            const resp: any = await apiFilterActivities({
                outletId,
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
                orderBy: '"createdAt" desc',
            })
            if (resp?.data == null) {
                setData([])
                setTotal(0)
                return
            }
            setData(resp.data.data ?? [])
            setTotal(Number(resp.data.total ?? 0))
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [outletId, currentPage, pageSize, setIsLoading])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleCreate = async (values: any) => {
        try {
            setIsLoading(true)
            const resp: any = await apiCreateActivity(values)
            if (resp.status === 200) {
                toastSuccess('Thêm tương tác thành công!')
                setIsOpenCreate(false)
                form.resetFields()
                fetchData()
                return
            }
            toastError('Có lỗi xảy ra!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async (id: string) => {
        try {
            setIsLoading(true)
            const resp = await apiDeleteActivity(id)
            if (resp.status === 200) {
                toastSuccess('Xoá thành công!')
                fetchData()
                return
            }
            toastError('Có lỗi xảy ra!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const columns: TableProps<IOutletActivity>['columns'] = [
        {
            key: 'STT',
            title: 'STT',
            render: (_, __, index) => ++index + (pageSize * (currentPage - 1)),
            width: 60,
        },
        {
            key: 'createdAt',
            title: 'Thời gian',
            dataIndex: 'createdAt',
            render: v => fmtDateTime(v),
        },
        {
            key: 'type',
            title: 'Loại',
            dataIndex: 'type',
            render: v => <Tag color='blue'>{labelFrom(ACTIVITY_TYPE_OPTIONS, v)}</Tag>,
        },
        {
            key: 'actor',
            title: 'Nhân sự',
            render: (_, r) => r.actorUserName || (r.actorUserId ? `#${r.actorUserId}` : '—'),
        },
        {
            key: 'outcome',
            title: 'Kết quả',
            dataIndex: 'outcome',
            render: v => labelFrom(ACTIVITY_OUTCOME_OPTIONS, v),
        },
        {
            key: 'durationMinutes',
            title: 'Thời lượng',
            dataIndex: 'durationMinutes',
            render: v => v ? `${v} phút` : '—',
        },
        {
            key: 'nextFollowupAt',
            title: 'Hẹn tiếp',
            dataIndex: 'nextFollowupAt',
            render: v => fmtDateTime(v),
        },
        {
            key: 'note',
            title: 'Nội dung',
            dataIndex: 'note',
        },
        {
            key: 'action',
            title: 'Tác vụ',
            align: 'center',
            width: 100,
            render: (_, r) => (
                canDelete ? <Button danger size='small' onClick={() => handleDelete(r.id)}>Xoá</Button> : null
            ),
        },
    ]

    return (
        <div>
            {canAdd && (
                <div style={{ textAlign: 'end', marginBottom: 16 }}>
                    <Button type='primary' onClick={() => setIsOpenCreate(true)}>+ Thêm tương tác</Button>
                </div>
            )}
            <CustomTable
                namePage='tương tác'
                rowKey='id'
                columns={columns}
                dataSource={data}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                title='Thêm tương tác mới'
                open={isOpenCreate}
                footer={null}
                onCancel={() => setIsOpenCreate(false)}
                destroyOnClose
            >
                <ActivityForm
                    form={form}
                    outletId={outletId}
                    defaultActorUserId={currentUserId || undefined}
                    onSubmit={handleCreate}
                    onCancel={() => setIsOpenCreate(false)}
                />
            </Modal>
        </div>
    )
}
